export type SalesLetterKind = "use" | "realm";

export interface SalesLetter {
  id: number;
  slug: string;
  kind: SalesLetterKind;
  /** URL keyword shown after `/amulet-for/` or `/amulet-from/` */
  intent: string;
  /** Short label used in cards & breadcrumbs */
  label: string;
  /** Above-fold eyebrow (small caps line) */
  eyebrow: string;
  /** H1 of the landing page */
  headline: string;
  /** Subheadline / promise */
  subheadline: string;
  /** Opening hook (1-2 short paragraphs) */
  hook: string;
  /** Pain / problem agitation paragraph */
  problem: string;
  /** Story bridge (mid-section) */
  story: string;
  /** Solution explanation (custom amulet bridge) */
  solution: string;
  /** 3 short proof bullets */
  proof: string[];
  /** Objection-handling FAQ pairs */
  faq: { q: string; a: string }[];
  /** Primary CTA button label */
  ctaLabel: string;
  /** SEO meta description */
  metaDescription: string;
  /** SEO keywords */
  keywords: string[];
}

const cta = "Begin Crafting My Amulet";

export const salesLetters: SalesLetter[] = [
  // ---------------- USE (8) ----------------
  {
    id: 1, kind: "use", intent: "protection", slug: "protection",
    label: "Protection",
    eyebrow: "For those who feel the unseen",
    headline: "A Custom Amulet Forged to Shield You From What Others Cannot See",
    subheadline: "Stop carrying the weight of jealousy, hostility, and invisible drains. Wear protection made for your name, your birth date, and your enemies.",
    hook: "You already know who they are. The coworker who smiles while wishing you'd fail. The relative whose advice always leaves you smaller. The stranger whose stare lingers a second too long. You feel it in your chest before you have words for it.",
    problem: "Generic 'protection' jewelry is a guess. It was made for nobody, so it answers to nobody. Meanwhile your sleep is shallow, your luck stalls, and the same arguments keep finding your door. You don't need another silver charm — you need a shield that knows your name.",
    story: "For fifteen years our master craftsman has forged amulets one at a time, hand to fire to stone. Each one is built around a single person — their birth chart, their threats, their intentions. Clients tell us the change is not loud. It is quiet. People stop calling for the wrong reasons. Doors that were stuck quietly open. The static thins.",
    solution: "Your custom protection amulet is built in three steps. You tell us what you carry. We align materials, sigils and a guardian spirit to your chart. It is consecrated during the closest favorable lunar window and shipped with its certificate of authenticity. You wear it. The work begins.",
    proof: [
      "Hand-forged stainless steel, charged during a favorable lunar window",
      "Built around your birth date, intentions, and named adversaries",
      "Ships with a sealed Certificate of Authenticity & care ritual",
    ],
    faq: [
      { q: "How fast will I feel a difference?", a: "Most clients report a noticeable shift in atmosphere within 7–14 days of daily wear." },
      { q: "Is this safe to wear all day?", a: "Yes. It's hypoallergenic stainless steel, water-safe, and designed to be worn 24/7." },
      { q: "What if I don't know who is harming me?", a: "You don't need to. Your amulet is keyed to your energy field, not their identity." },
    ],
    ctaLabel: cta,
    metaDescription: "A custom-forged protection amulet built around your birth date, intentions, and named threats. Hand-charged. Ships worldwide.",
    keywords: ["protection amulet", "custom protection talisman", "evil eye protection", "personal protection jewelry"],
  },
  {
    id: 2, kind: "use", intent: "love", slug: "love-romance",
    label: "Love & Romance",
    eyebrow: "For the heart that refuses to settle",
    headline: "An Amulet Crafted to Magnetize the Love Your Heart Was Promised",
    subheadline: "Whether you want to be found, deepen what you have, or call back what was lost — your custom amulet is keyed to one heart: yours.",
    hook: "You can feel it. The kind of love you were built for is real, and it has a face you have not yet seen — or one you already know but can no longer reach. Either way, the longing isn't a weakness. It's a signal.",
    problem: "Apps shuffle. Friends play matchmaker. You journal, manifest, and try not to seem desperate. And still — nothing that lands. Generic 'love' charms are made for everyone, which is to say, no one. They cannot pull from your specific blueprint.",
    story: "We have crafted love amulets for clients who married within ten months of wearing them. For couples who returned from the brink. For widows who never expected to feel butterflies again. Every story starts the same way: a chart, a name, an intention sealed in metal.",
    solution: "Your custom love amulet is forged with the materials your chart accepts and the spirit family aligned to your romantic blueprint. It is charged during a Venusian window and shipped with a 30-day ritual of awakening. You don't push. You wear. The right person walks toward you.",
    proof: [
      "Built from your natal data, romantic intent, and ideal partner profile",
      "Consecrated during Venus-favorable lunar timing",
      "Includes 30-day awakening ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "Can I name a specific person?", a: "Yes. We craft both 'attract the right one' and 'reconcile a specific bond' versions — you'll choose during the form." },
      { q: "Will this work if I'm already in a relationship?", a: "Absolutely. Many clients use it to deepen intimacy, restore desire, or weather a hard season." },
      { q: "Is this manipulation?", a: "No. The amulet aligns your field — it cannot override another person's free will. It removes interference." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom love amulet crafted to your chart and intentions. Attract, deepen, or reconcile. Hand-forged and Venus-charged.",
    keywords: ["love amulet", "romance talisman", "attract love", "reconciliation amulet", "custom love charm"],
  },
  {
    id: 3, kind: "use", intent: "wealth", slug: "wealth-abundance",
    label: "Wealth & Abundance",
    eyebrow: "For builders, founders, and those done playing small",
    headline: "A Custom Amulet Built to Open the Channels Money Has Been Avoiding",
    subheadline: "Not luck. Not hustle. A direct alignment between your chart, your work, and the spirits of opportunity.",
    hook: "You work hard. You're not afraid of the long hours. So why does the money move sideways instead of up? Why do the deals come close — then quietly disappear? You suspect what most successful people eventually admit: there is a layer to wealth that is not strategic.",
    problem: "Generic 'abundance' bracelets fix nothing. You need something forged for your blueprint, your trade, and the specific blockages your chart carries. A talisman keyed to opportunity, not vague positivity.",
    story: "We've crafted wealth amulets for traders who broke yearly records, for therapists whose practice doubled, for two restaurant owners who paid off debt within a single quarter of wear. The pattern is the same: the channels open, and the work begins to land.",
    solution: "Your custom wealth amulet is forged with materials of expansion, sigils of opportunity, and a spirit aligned to your line of work. It is charged in a Jupiter-favorable window and shipped with a daily activation ritual.",
    proof: [
      "Aligned to your trade, your chart, and your specific revenue blockages",
      "Charged during Jupiter-favorable lunar timing",
      "Ships with daily activation ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "Will this make me rich overnight?", a: "No. It opens channels. The work is yours. But clients consistently report opportunities that previously refused to land." },
      { q: "Does it work for any profession?", a: "Yes — we tune it to your specific trade in the order form." },
      { q: "Can I combine it with a protection amulet?", a: "Yes, and many of our highest-earning clients do exactly that." },
    ],
    ctaLabel: cta,
    metaDescription: "A custom wealth amulet aligned to your chart, trade, and revenue blockages. Hand-forged and Jupiter-charged.",
    keywords: ["wealth amulet", "money talisman", "abundance amulet", "prosperity charm", "business amulet"],
  },
  {
    id: 4, kind: "use", intent: "healing", slug: "healing",
    label: "Healing",
    eyebrow: "For the body and the heart that are tired",
    headline: "A Healing Amulet, Custom-Forged to the Wound Only You Can Name",
    subheadline: "Recovery is not a straight line. Your amulet is built to walk it with you — emotionally, energetically, and physically.",
    hook: "Some wounds are loud. Most are not. They are the chronic exhaustion no one sees. The grief you carry to work. The body that flinches before you do. You are not broken — you are carrying too much without backup.",
    problem: "Mass-produced 'healing crystals' are blind. They don't know what you're recovering from, what your chart can absorb, or what season of life you're in. A custom amulet does.",
    story: "Our healing amulets have walked clients through divorce, chemotherapy, postpartum, and grief that had no name. The amulet does not replace medicine or therapy. It holds the field steady while you do the work.",
    solution: "Your custom healing amulet is forged with restorative materials and sigils of repair, aligned to your chart and the specific season you are in. It is consecrated under a healing moon and shipped with a gentle daily ritual.",
    proof: [
      "Built around your specific recovery — emotional, physical, or spiritual",
      "Charged under a healing-favorable lunar window",
      "Ships with gentle daily ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "Is this a substitute for medical care?", a: "No. It is a companion to it. Continue all medical treatment your provider recommends." },
      { q: "Can I gift it to someone I love?", a: "Yes — provide their birth date and intentions in the form." },
      { q: "Will it help with grief?", a: "Yes. Grief is one of the most common reasons clients order this amulet." },
    ],
    ctaLabel: cta,
    metaDescription: "A custom healing amulet for emotional, physical, or spiritual recovery. Aligned to your chart and your season of life.",
    keywords: ["healing amulet", "grief talisman", "recovery amulet", "emotional healing jewelry"],
  },
  {
    id: 5, kind: "use", intent: "intuition", slug: "intuition-awakening",
    label: "Intuition & Awakening",
    eyebrow: "For those who already see, but want to see clearly",
    headline: "A Custom Amulet to Sharpen Your Inner Sight, Open Your Dreams, and Awaken What You Already Carry",
    subheadline: "Stop second-guessing your gut. Your amulet is keyed to your chart's psychic signature.",
    hook: "You have always sensed more than you said out loud. Dreams that landed. Hunches that turned out true. A knowing that arrived seconds before the phone rang. You're not imagining it.",
    problem: "Most 'intuition' tools assume one signal works for everyone. They don't. Your psychic blueprint is unique. A generic talisman cannot tune to a frequency it doesn't know.",
    story: "Our awakening amulets have been worn by working mediums, dreamers, tarot readers and quiet receivers who never identified as 'spiritual.' All report the same effect: the signal stops fading in and out.",
    solution: "Your custom awakening amulet is forged with materials of clarity, sigils of inner sight, and a spirit aligned to your psychic signature. It is charged during a third-eye favorable lunar window.",
    proof: [
      "Tuned to your chart's specific psychic signature",
      "Charged during a third-eye favorable lunar window",
      "Ships with dream journal ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "I'm a beginner. Will this overwhelm me?", a: "No. The amulet opens at your pace. You stay in control." },
      { q: "Will I see spirits?", a: "Some clients do, most experience sharper hunches and clearer dreams instead." },
      { q: "Can I wear it during meditation?", a: "Yes — it amplifies meditation work significantly." },
    ],
    ctaLabel: cta,
    metaDescription: "A custom awakening amulet tuned to your psychic signature. Sharpen intuition, open dreams, awaken inner sight.",
    keywords: ["intuition amulet", "psychic awakening talisman", "third eye amulet", "dream amulet"],
  },
  {
    id: 6, kind: "use", intent: "courage", slug: "courage-confidence",
    label: "Courage & Confidence",
    eyebrow: "For the decision you've been postponing",
    headline: "A Custom Warrior Amulet Forged to Make You Move",
    subheadline: "When fear has run the show too long, your amulet becomes the spine you didn't know you had.",
    hook: "You know what you have to do. You've known for weeks, months, maybe years. The conversation. The resignation. The boundary. The leap. Knowing isn't the problem. Moving is.",
    problem: "Affirmations don't fix a nervous system that has been bracing for years. Generic 'confidence' charms are decoration. You need something keyed to your fear pattern — and your fight.",
    story: "We've forged warrior amulets for women who finally left, founders who finally launched, sons who finally spoke up, and athletes who finally believed. The amulet does not erase fear. It puts steel in your hand while you walk through it.",
    solution: "Your custom warrior amulet is forged with materials of fortitude, sigils of decisive action, and a spirit aligned to your specific fight. Charged under a Mars-favorable window.",
    proof: [
      "Built around your specific fear and your specific fight",
      "Charged during Mars-favorable lunar timing",
      "Ships with a 7-day courage ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "Will it make me reckless?", a: "No. It sharpens decision-making, it doesn't override it." },
      { q: "Can I wear it to interviews or hard conversations?", a: "Absolutely — that's exactly what it's designed for." },
      { q: "Will I feel different right away?", a: "Most clients report a shift in posture and clarity within the first week." },
    ],
    ctaLabel: cta,
    metaDescription: "A custom warrior amulet for courage, decisive action, and confidence. Hand-forged for your specific fight.",
    keywords: ["courage amulet", "confidence talisman", "warrior amulet", "decision-making amulet"],
  },
  {
    id: 7, kind: "use", intent: "family", slug: "family-home",
    label: "Family & Home",
    eyebrow: "For the keeper of the household",
    headline: "A Custom Amulet to Protect Your Children, Harmonize Your Home, and Hold the Field Steady",
    subheadline: "Built around your family's birth dates and the specific tension your home carries.",
    hook: "You are the one who notices first. The mood that walks in the door. The argument before it lands. The child who is suddenly quiet. You hold a field most people never see.",
    problem: "Generic home blessings can't carry the weight of a real family. Your amulet must know who lives under your roof — and what is trying to enter.",
    story: "Our family amulets have steadied homes through divorces, illnesses, teenage storms, and grief. They do not magic away conflict. They give the household a center to return to.",
    solution: "Your custom family amulet is forged with materials of harmony, keyed to the birth dates of your inner circle, and consecrated under a Cancer-favorable window. Worn by you — felt by all.",
    proof: [
      "Aligned to the birth dates of your inner family circle",
      "Charged during Cancer-favorable lunar timing",
      "Ships with a hearth blessing ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "Should each family member have one?", a: "No — one amulet on the household keeper holds the field for all." },
      { q: "Will it help with a specific child?", a: "Yes. You'll specify in the form whose protection is your priority." },
      { q: "Can I hang it at home instead of wearing it?", a: "Wearing is most effective, but a home placement variant is available — note it in the form." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom family amulet aligned to your loved ones' birth dates. Protects children, harmonizes home, holds the field.",
    keywords: ["family amulet", "home protection talisman", "child protection amulet", "household harmony"],
  },
  {
    id: 8, kind: "use", intent: "rebirth", slug: "rebirth-transformation",
    label: "Rebirth & Transformation",
    eyebrow: "For the version of you that is rising",
    headline: "A Custom Amulet to Mark the End of an Era — and Forge the Next One",
    subheadline: "After loss, after divorce, after the version of you that died — your amulet is built for who you are becoming.",
    hook: "Something ended. You knew it would, and it still broke you. Now you're standing in the in-between, carrying a self that no longer fits and a future you can't yet see.",
    problem: "Most jewelry is a memento. You don't need a memento. You need a marker — a forged object that says clearly: this is the line, and I crossed it.",
    story: "Our rebirth amulets have walked clients out of marriages, addictions, careers, and identities that almost killed them. Each one is the first object of a new life.",
    solution: "Your custom rebirth amulet is forged with materials of transformation, sigils of crossing, and a spirit aligned to phoenix energy. Charged on a new moon — the cleanest threshold of the cycle.",
    proof: [
      "Built around the era you're closing and the one you're stepping into",
      "Charged on a new moon for clean threshold work",
      "Ships with a closing ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "I don't know who I'm becoming yet — is that ok?", a: "Yes. The amulet doesn't need a destination. It just needs the threshold." },
      { q: "Can I wear my old jewelry too?", a: "We recommend retiring or cleansing pieces tied to the closed chapter." },
      { q: "Is this appropriate for grief?", a: "Yes — it's one of the most common contexts. It honors the loss while marking the rise." },
    ],
    ctaLabel: cta,
    metaDescription: "A custom rebirth amulet for the threshold between who you were and who you are becoming. Forged for transformation.",
    keywords: ["rebirth amulet", "transformation talisman", "phoenix amulet", "divorce recovery jewelry", "new chapter amulet"],
  },

  // ---------------- REALM (7) ----------------
  {
    id: 9, kind: "realm", intent: "angelic", slug: "angelic",
    label: "Angelic Realm",
    eyebrow: "Drawn from the choirs of light",
    headline: "An Angelic Amulet, Forged to Bind You to a Guardian by Name",
    subheadline: "Not a generic 'angel charm' — a custom amulet keyed to the specific angelic intelligence that aligns with your chart.",
    hook: "You have felt them. The presence in the room when no one is there. The save that should not have happened. The voice that said don't, and you didn't.",
    problem: "Generic angel jewelry is iconography. It points to a category — not a presence. You deserve to be bound to a specific guardian, by their seal and their name.",
    story: "Our angelic amulets have been worn by mothers, soldiers, ER nurses, and anyone walking through dangerous seasons. Clients consistently describe a felt presence — a weight beside them — within weeks.",
    solution: "Your custom angelic amulet is forged with materials of light, the seal of the angelic intelligence aligned to your chart, and consecrated on a Sunday at first light.",
    proof: [
      "Bound to the specific angelic intelligence aligned with your chart",
      "Consecrated on Sunday at first light",
      "Ships with the angel's seal, name, and invocation ritual",
    ],
    faq: [
      { q: "Do I need to be religious?", a: "No. Angelic intelligences pre-date and outlive every religion." },
      { q: "Can I request a specific archangel?", a: "Yes — note your preference in the form, otherwise we'll align to your chart." },
      { q: "Is this safe for children to wear?", a: "Yes, and it's a common request for newborns and young children." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom angelic amulet bound to the guardian aligned with your chart. Forged with seal, name, and invocation.",
    keywords: ["angelic amulet", "guardian angel talisman", "archangel amulet", "angelic protection"],
  },
  {
    id: 10, kind: "realm", intent: "demonic", slug: "demonic",
    label: "Demonic Realm",
    eyebrow: "For practitioners ready to work with raw power",
    headline: "A Demonic Amulet, Forged for Those Who Have Outgrown Polite Magic",
    subheadline: "Fierce protection, taboo desires, and pacts of power — bound by name, sealed by metal, charged on the dark moon.",
    hook: "You have outgrown the soft language. You don't want guidance — you want results. You've already worked with the lighter realms, and they served you. Now you're ready for the deeper voltage.",
    problem: "Most occult jewelry is decorative — pentagrams sold as fashion. You need a working tool: a real bound amulet, with the seal of a specific intelligence and the discipline to wield it.",
    story: "Our demonic amulets are crafted only for clients who confirm their intent and discipline. They are not for everyone. Those who wear them describe a sharper edge to their lives — fewer obstacles, faster results, and a presence that does not tolerate weakness.",
    solution: "Your custom demonic amulet is forged with materials of power, the seal of the intelligence you're aligned with, and consecrated on the dark moon. Includes a strict working protocol.",
    proof: [
      "Bound to the specific demonic intelligence aligned with your work",
      "Consecrated on the dark moon for full voltage",
      "Ships with seal, name, and a strict working protocol",
    ],
    faq: [
      { q: "Is this dangerous?", a: "Power without discipline always is. The protocol exists for a reason — follow it." },
      { q: "Can I combine with an angelic amulet?", a: "We do not recommend wearing both simultaneously." },
      { q: "Do you screen orders?", a: "Yes — we may follow up before crafting demonic-realm amulets." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom demonic amulet bound to a named intelligence. Hand-forged, dark-moon charged, ships with a working protocol.",
    keywords: ["demonic amulet", "goetia talisman", "occult amulet", "fierce protection talisman"],
  },
  {
    id: 11, kind: "realm", intent: "elemental", slug: "elemental",
    label: "Elemental Realm",
    eyebrow: "Drawn from fire, water, earth, and air",
    headline: "An Elemental Amulet Forged to the Element Your Chart Is Starving For",
    subheadline: "When you keep burning out, drying up, drifting, or sinking — your chart is telling you which element to bring back.",
    hook: "Burnout. Restlessness. Brain fog. Anxiety. We treat these as problems. They are also signals: an element in your chart is depleted.",
    problem: "Generic elemental jewelry assigns by zodiac sun sign — which is too crude. Your full chart reveals which element you actually need to call back, and at what dosage.",
    story: "Our elemental amulets restore balance the way nothing else can. Fire clients regain drive. Water clients soften and sleep. Earth clients regain solidity. Air clients regain clarity.",
    solution: "Your custom elemental amulet is forged with the natural materials of your needed element, sigils of the elemental court, and consecrated outdoors in that element's domain.",
    proof: [
      "Aligned to the specific element your chart is depleted in",
      "Consecrated outdoors in that element's domain",
      "Ships with element-tending ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "What if I'm depleted in more than one element?", a: "We tune to the most acute deficit and address others through ritual." },
      { q: "Can I choose the element?", a: "Yes — note your preference in the form." },
      { q: "Is this safe to wear in water?", a: "Yes — stainless steel is fully water-safe." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom elemental amulet aligned to the element your chart is depleted in. Restore fire, water, earth, or air.",
    keywords: ["elemental amulet", "fire talisman", "water amulet", "earth talisman", "air amulet"],
  },
  {
    id: 12, kind: "realm", intent: "faerie", slug: "faerie",
    label: "Faerie Realm",
    eyebrow: "For those whose joy was stolen",
    headline: "A Faerie Amulet, Forged to Restore the Glamour, Luck and Wonder That Was Taken From You",
    subheadline: "Built with offerings to the fae — bound to bring delight, beauty and serendipitous luck back into your daily field.",
    hook: "Remember when ordinary days had glints in them? Coincidences that made you laugh? Strangers who smiled at the perfect moment? Adulthood doesn't have to be the death of that.",
    problem: "Most jewelry is heavy — protection, power, sealing. You don't need more weight. You need the lightness back.",
    story: "Our faerie amulets are worn by clients who describe their daily lives changing in small, magical ways: parking spots, perfect timings, brief encounters that lift the day.",
    solution: "Your custom faerie amulet is forged with natural materials, offerings sealed inside, sigils of the fae courts, and consecrated at dawn or dusk — the threshold hours.",
    proof: [
      "Aligned with the faerie court matched to your chart",
      "Consecrated at dawn or dusk threshold hours",
      "Ships with offerings, glamour ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "Is this 'just for fun'?", a: "No — luck and joy are real currencies. This amulet treats them seriously." },
      { q: "Can it backfire?", a: "Honor the offerings ritual and it will not." },
      { q: "Is this good for creatives?", a: "Exceptionally — it's our top seller for artists and writers." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom faerie amulet to restore glamour, luck, and wonder. Bound to the fae court aligned with your chart.",
    keywords: ["faerie amulet", "fae talisman", "luck amulet", "glamour talisman"],
  },
  {
    id: 13, kind: "realm", intent: "ancestral", slug: "ancestral",
    label: "Ancestral Realm",
    eyebrow: "For the descendant carrying the line",
    headline: "An Ancestral Amulet, Forged to Bind You to the Power Already Inside Your Bloodline",
    subheadline: "Your ancestors are waiting. Your amulet becomes the bridge.",
    hook: "There is wisdom in your line. Not vague — specific. A grandmother who saw. A great-uncle who survived what he should not have. A name in your family tree that hums when you say it. They are still working.",
    problem: "Generic 'ancestor' jewelry is sentimentality. You need a real bridge: a forged object that calls your line by name and gives them a place to land.",
    story: "Our ancestral amulets have reconnected adoptees to their lineages, opened dreams that delivered specific names, and given practitioners access to wisdom they were never formally taught.",
    solution: "Your custom ancestral amulet is forged with materials of bone-memory, sigils of the line, and consecrated at a graveside or earth-marker. Ships with an altar setup ritual.",
    proof: [
      "Built around your bloodline and chosen ancestral focus",
      "Consecrated with earth and bone-memory materials",
      "Ships with altar setup ritual + Certificate of Authenticity",
    ],
    faq: [
      { q: "What if I don't know my ancestry?", a: "You don't need to. Your blood does." },
      { q: "Can adoptees use this?", a: "Yes — adoptive AND birth lines can be honored. Specify in the form." },
      { q: "Will I have ancestor dreams?", a: "Many clients do, especially in the first 30 days." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom ancestral amulet binding you to the power of your bloodline. Forged with bone-memory materials.",
    keywords: ["ancestral amulet", "lineage talisman", "ancestor amulet", "bloodline jewelry"],
  },
  {
    id: 14, kind: "realm", intent: "celestial", slug: "celestial",
    label: "Celestial Realm",
    eyebrow: "Drawn from planets and stars",
    headline: "A Celestial Amulet, Forged to Your Specific Stellar Signature",
    subheadline: "Your birth chart is a map. Your amulet is the key — bound to your dominant planet, your healing star, or the cosmic timing you need most.",
    hook: "Astrology can describe you. A celestial amulet can move you. The difference is the bind: a forged object keyed to a specific stellar signal in your chart.",
    problem: "Most 'zodiac' jewelry is sun-sign decoration. Your chart is bigger than that. You need an amulet keyed to a specific planetary force you actually need.",
    story: "Our celestial amulets have been worn by astrologers themselves — by clients who know exactly what their chart is missing and want a forged tool to call it in.",
    solution: "Your custom celestial amulet is forged with the metal of the chosen planet, sigils of the celestial court, and consecrated during that planet's hour and day. Includes timing chart.",
    proof: [
      "Forged in the metal of the planet your chart needs most",
      "Consecrated during the planetary hour and day",
      "Ships with personal timing chart + Certificate of Authenticity",
    ],
    faq: [
      { q: "I don't know my chart well — can you help?", a: "Yes — give us your birth date, time, and place and we'll align." },
      { q: "Can I request a specific planet?", a: "Yes, override the chart-based recommendation in the form." },
      { q: "Is this for astrology beginners?", a: "Yes — many beginners find this is where their practice clicks." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom celestial amulet forged to your stellar signature. Planetary metal, planetary hour, with timing chart.",
    keywords: ["celestial amulet", "planetary talisman", "astrology amulet", "zodiac talisman"],
  },
  {
    id: 15, kind: "realm", intent: "shadow", slug: "shadow-underworld",
    label: "Shadow / Underworld",
    eyebrow: "For the deep work no one else will do",
    headline: "A Shadow Amulet, Forged for Banishing, Cord-Cutting, and the Work Done Below the Surface",
    subheadline: "Built for practitioners and seekers ready to do the descent — and return whole.",
    hook: "You're done pretending. There are patterns in your life that didn't start with you. Cords still attached to the wrong people. Vows you made before you knew yourself. Work that lives in the basement, not the living room.",
    problem: "Light-only spirituality cannot complete this work. You need a tool forged for the dark — disciplined, safe, and built to bring you back.",
    story: "Our shadow amulets have walked clients through cord-cuttings, vow-revocations, family-line clearings, and underworld initiations. Worn carefully, they are some of the most transformative objects we make.",
    solution: "Your custom shadow amulet is forged with materials of dissolution, sigils of the underworld court, and consecrated on the dark moon at midnight. Ships with a strict descent-and-return protocol.",
    proof: [
      "Built for banishing, cord-cutting, and underworld work",
      "Consecrated on the dark moon at midnight",
      "Ships with descent-and-return protocol + Certificate of Authenticity",
    ],
    faq: [
      { q: "Is this safe?", a: "It is — when you follow the protocol. Without it, no shadow tool is safe." },
      { q: "Should I have a teacher?", a: "Highly recommended for first-time underworld work." },
      { q: "Can I wear it daily?", a: "Only during active working cycles. Otherwise, store on its altar." },
    ],
    ctaLabel: cta,
    metaDescription: "Custom shadow amulet for banishing, cord-cutting, and underworld work. Forged with strict working protocol.",
    keywords: ["shadow amulet", "banishing talisman", "underworld amulet", "cord-cutting jewelry"],
  },
];

export const getLetterBySlug = (slug: string): SalesLetter | undefined =>
  salesLetters.find((l) => l.slug === slug);

export const useLetters = salesLetters.filter((l) => l.kind === "use");
export const realmLetters = salesLetters.filter((l) => l.kind === "realm");
